import { create } from 'zustand'

import getCurrentSession, {
	flushCurrentSession,
} from '@/services/sessionService'
import favouritesStore from '@/stores/favouritesStore'
import useProjectStore from '@/stores/projectStore'

export interface SessionState {
	session: Session | null
	deviceType: 'mobile' | 'desktop'
	loadingSession: boolean
	hasHydrated: boolean
	hydrate: () => Promise<void>
	fetchSession: () => Promise<void>
	setSession: (session: Session | null) => void
	setDeviceType: (deviceType: 'mobile' | 'desktop') => void
	setHasHydrated: (value: boolean) => void
	createExpirationHandler: () => (event: MouseEvent) => void
	toggleFavourite: (trackId: number) => void
	clearAllFavourites: () => void
	refreshSession: () => Promise<void>
	resetSession: () => void
}

const initialSession: Session = {
	user: null,
	expires: null,
} as Session

export const isSessionActive = (
	session: Session | null | undefined
): boolean => {
	if (!session?.expires) return true
	return new Date(session.expires) >= new Date()
}

const detectDeviceType = (): 'mobile' | 'desktop' =>
	typeof window === 'undefined'
		? 'desktop'
		: window.matchMedia('(hover: none)').matches ||
		  window.matchMedia('(any-hover: none)').matches ||
		  window.matchMedia('(pointer: coarse)').matches ||
		  window.matchMedia('(any-pointer: coarse)').matches
		? 'mobile'
		: 'desktop'

const useSessionStore = create<SessionState>()((set, get) => ({
	session: initialSession,
	deviceType: 'desktop',
	loadingSession: true,
	hasHydrated: false,

	hydrate: async (): Promise<void> => {
		if (typeof window !== 'undefined') {
			set({ deviceType: detectDeviceType() })
		}
		await get().fetchSession()
	},

	fetchSession: async (): Promise<void> => {
		const sessionData = await getCurrentSession()
		set({
			session: sessionData ?? initialSession,
			loadingSession: false,
			hasHydrated: true,
		})

		if (sessionData?.user) {
			favouritesStore
				.getState()
				.setFavouriteTracks(sessionData.user.favouriteTracks || [])
			useProjectStore.getState().getProjects()
		}
	},

	setSession: (sessionData: Session | null): void => {
		if (sessionData == null) return
		set({
			session: sessionData,
			loadingSession: false,
			hasHydrated: true,
		})
		if (sessionData?.user) {
			favouritesStore
				.getState()
				.setFavouriteTracks(sessionData.user.favouriteTracks || [])
			useProjectStore.getState().getProjects()
		}
	},

	setDeviceType: (deviceType: 'mobile' | 'desktop'): void => {
		set({ deviceType })
	},

	setHasHydrated: (value: boolean): void => {
		set({ hasHydrated: value })
	},

	createExpirationHandler: (): ((event: MouseEvent) => void) => {
		return (event: MouseEvent): void => {
			const { session } = get()
			if (isSessionActive(session)) return

			const target = event.target as HTMLElement
			const linkOrButton = target.closest('a, button, input')
			if (linkOrButton) {
				event.preventDefault()
				window?.aa?.('setAuthenticatedUserToken', undefined)
				const returnUrl = encodeURIComponent(
					window.location.pathname + window.location.search
				)
				window.location.href = `/login?returnUrl=${returnUrl}`
			}
		}
	},

	toggleFavourite: (trackId: number): void => {
		void flushCurrentSession()
		const { session } = get()
		if (!session?.user?.favouriteTracks) return

		const { favouriteTracks } = session.user
		const isFavourite = favouriteTracks.some((id) => id === trackId)

		set({
			session: {
				...session,
				user: {
					...session.user,
					favouriteTracks: isFavourite
						? favouriteTracks.filter((id) => id !== trackId)
						: [...favouriteTracks, trackId],
				},
			},
		})
	},

	clearAllFavourites: (): void => {
		void flushCurrentSession()
		const { session } = get()
		if (!session?.user) return

		set({
			session: {
				...session,
				user: {
					...session.user,
					favouriteTracks: [],
				},
			},
		})
	},

	refreshSession: async (): Promise<void> => {
		await flushCurrentSession()
		const newSession = await getCurrentSession()
		set({ session: newSession ?? initialSession })
	},

	resetSession: (): void => {
		set({
			session: initialSession,
			loadingSession: true,
			hasHydrated: false,
		})
	},
}))

export default useSessionStore
